import React, { useState } from 'react';
import classnames from 'classnames';
import { CommonInputProps } from '../types';
import { InputMessage } from '../input-message';

import styles from './text-area.module.scss';

const baseClass = 'text-area';

export type TextAreaProps = {
  characterCount?: number;
  value: string;
} & CommonInputProps &
  Pick<
    React.InputHTMLAttributes<HTMLTextAreaElement>,
    'minLength' | 'maxLength' | 'onFocus' | 'onBlur' | 'onChange' | 'autoFocus' | 'autoComplete'
  >;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      minLength,
      maxLength,
      onFocus,
      onBlur,
      onChange,
      name,
      value,
      className,
      id,
      disabled,
      required,
      characterCount = 0,
      message,
      isInvalid,
      autoFocus,
      autoComplete
    },
    ref
  ) => {
    const [hasValue, setHasValue] = useState('');
    const classes = classnames(
      styles[baseClass],
      { [styles[`${baseClass}--error`]]: isInvalid },
      { [styles[`${baseClass}--message`]]: message },
      { [styles[`${baseClass}--disabled`]]: disabled },
      { [styles[`${baseClass}--has-value`]]: hasValue },
      className
    );
    const labelId = `${id}__label`;
    const messageId = `${id}__message`;

    const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }

      setHasValue(e.target?.value);
    };

    return (
      <div className={classes}>
        <div className={styles[`${baseClass}__input-wrapper`]}>
          <textarea
            className={styles[`${baseClass}__input`]}
            id={id}
            disabled={disabled}
            name={name}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            placeholder=" " // empty space as placeholder enables css selector :placholder-shown
            value={value}
            onChange={handleOnChange}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={ref}
            aria-invalid={Boolean(isInvalid)}
            aria-labelledby={labelId}
            aria-describedby={message ? messageId : undefined}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
          />
          <label htmlFor={id} className={styles[`${baseClass}__label`]} id={labelId}>
            {label}
          </label>
        </div>
        <div className={styles[`${baseClass}__additional-info`]}>
          {message && (
            <InputMessage
              id={messageId}
              disabled={disabled}
              isError={isInvalid}
              className={styles[`${baseClass}__input-message`]}
              message={message}
            />
          )}
          <span className={styles[`${baseClass}__character-count`]}>
            {characterCount >= 0 && maxLength && characterCount + '/' + maxLength}
          </span>
        </div>
      </div>
    );
  }
);
TextArea.displayName = 'TextArea';
